import { useAppKit, useAppKitAccount } from '@reown/appkit/react';
import { useDisconnect } from 'wagmi';
import { useWallet } from '@solana/wallet-adapter-react';
import { Wallet, ChevronDown, Copy, Check, LogOut, Link } from 'lucide-react';
import { useNetworkModal } from './NetworkModal';
import { useState, useRef, useEffect } from 'react';
import { useWalletModal } from './SolanaWalletModal';
import { saveWalletConnection } from '../services/supabase';
import { useReferral } from '../hooks/useReferral';

interface WalletButtonProps {
  className?: string;
  network?: 'evm' | 'sol' | 'auto';
}

export function WalletButton({ className = '', network = 'auto' }: WalletButtonProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeWallet, setActiveWallet] = useState<'evm' | 'sol' | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const { address: evmAddress, isConnected: isEvmConnected } = useAppKitAccount();
  const { publicKey: solanaAddress, connected: isSolanaConnected, disconnect: disconnectSolana } = useWallet();
  const { disconnect: disconnectEvm } = useDisconnect();
  const { setOpen: openNetworkModal } = useNetworkModal();
  const { setOpen: openSolanaModal } = useWalletModal();
  const { referrerAddress } = useReferral();
  
  // Track connection changes to save referral info
  const [lastConnectedAddress, setLastConnectedAddress] = useState<string | null>(null);

  // Determine which wallet is connected, prioritizing based on the network prop
  useEffect(() => {
    // Update the active wallet type based on connections
    if (network === 'evm' && isEvmConnected) {
      setActiveWallet('evm');
    } else if (network === 'sol' && isSolanaConnected) {
      setActiveWallet('sol');
    } else if (network === 'auto') {
      // In auto mode, show any connected wallet with priority to the one specified
      if (isEvmConnected) {
        setActiveWallet('evm');
      } else if (isSolanaConnected) {
        setActiveWallet('sol');
      } else {
        setActiveWallet(null);
      }
    } else {
      setActiveWallet(null);
    }
  }, [isEvmConnected, isSolanaConnected, network]);

  const isConnected = activeWallet !== null;
  const address = 
    activeWallet === 'evm' ? evmAddress : 
    activeWallet === 'sol' ? solanaAddress?.toBase58() : 
    undefined;
  
  // Use the full wallet address in the referral link for proper database matching
  const referralLink = `${window.location.origin}/?ref=${address}`;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isConnected) {
      // Open appropriate wallet modal based on network
      if (network === 'sol') {
        openSolanaModal(true);
      } else {
        openNetworkModal(true);
      }
    } else {
      setShowDropdown(!showDropdown);
    }
  };

  const handleDisconnect = async () => {
    if (activeWallet === 'evm') {
      await disconnectEvm();
    } else if (activeWallet === 'sol') {
      await disconnectSolana();
    }
    setShowDropdown(false);
  };

  const handleCopyReferral = async () => {
    if (address) {
      try {
        await navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Silent fail
      }
    }
  };

  // Get the wallet type label for display
  const getWalletTypeLabel = () => {
    if (activeWallet === 'evm') return 'ETH';
    if (activeWallet === 'sol') return 'SOL';
    return '';
  };

  // Save wallet connection with referral info when a wallet connects
  useEffect(() => {
    const saveConnection = async () => {
      // If a wallet is connected and it's different from the last one we processed
      if (address && address !== lastConnectedAddress) {
        try {
          await saveWalletConnection({
            wallet_address: address,
            referrer_address: referrerAddress || undefined,
            wallet_type: activeWallet === 'evm' ? 'evm' : 'sol'
          });
          
          // Update last connected address to prevent duplicate saves
          setLastConnectedAddress(address);
        } catch (error) {
          // Silent fail
        }
      }
    };
    
    if (isConnected && address) {
      saveConnection();
    }
  }, [address, activeWallet, isConnected, referrerAddress, lastConnectedAddress]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleClick}
        className={`quantum-button py-2 px-4 flex items-center justify-center gap-2 ${className}`}
      >
        <Wallet className="w-4 h-4" />
        <span>
          {isConnected && address
            ? `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
            : network === 'sol' ? 'Connect Solana' : network === 'evm' ? 'Connect Ethereum' : 'Connect Wallet'}
        </span>
        {isConnected && (
          <ChevronDown 
            className={`w-4 h-4 transition-transform duration-200 ${
              showDropdown ? 'rotate-180' : ''
            }`} 
          />
        )}
      </button>

      {showDropdown && isConnected && (
        <div className="absolute right-0 mt-2 w-72 quantum-card p-2 z-50">
          <div className="space-y-2">
            <div className="quantum-card p-3 space-y-2">
              <div className="text-xs text-quantum-light/70">Your Referral Link</div>
              <div className="flex items-center gap-2">
                <div className="quantum-card flex-1 p-2 text-xs text-quantum-light/70 overflow-x-auto whitespace-nowrap">
                  {window.location.origin}/?ref={address?.substring(0, 6)}...{address?.substring(address.length - 4)}
                </div>
                <button 
                  onClick={handleCopyReferral}
                  className="quantum-button !p-2 min-w-[32px] flex items-center justify-center"
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={handleDisconnect}
              className="w-full quantum-card p-3 flex items-center gap-2 hover:bg-white/5 transition-colors duration-200"
            >
              <LogOut className="w-4 h-4 text-quantum-primary" />
              <span className="text-quantum-light">Disconnect</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}